import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TaskList from '../components/task/TaskList';
import TaskDialog from '../components/task/TaskDialog';
import Button from '../components/common/Button';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';

/**
 * Tasks-Komponente zur Verwaltung von Aufgaben
 * 
 * @param {Object} props - Komponenteneigenschaften
 * @param {Function} props.setLoggedIn - Funktion zum Setzen des Login-Status
 */
const Tasks = ({ setLoggedIn }) => {
    // Zustandsverwaltung für Dialoge und Daten
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [taskData, setTaskData] = useState({
        name: '',
        hourlyRate: '',
        dailyRate: '',
    });
    const [tasks, setTasks] = useState([]);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    
    // Hooks verwenden
    const navigate = useNavigate();
    const { isAuthenticated, validateAndRefreshToken } = useAuth(setLoggedIn);
    const { loading, error, fetchTasks: apiFetchTasks, addTask, updateTask, deleteTask } = useApi();

    // Authentifizierung überprüfen
    useEffect(() => {
        validateAndRefreshToken();
        if (!isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, validateAndRefreshToken, navigate]);

    // Aufgaben laden
    useEffect(() => {
        if (isAuthenticated) {
            fetchTasks();
        }
    }, [isAuthenticated]);

    /**
     * Lädt alle Aufgaben vom Server
     */
    const fetchTasks = async () => {
        try {
            const result = await apiFetchTasks();
            setTasks(result);
        } catch (error) {
            console.error('Fehler beim Laden der Aufgaben:', error);
        }
    };

    /**
     * Aktualisiert die Aufgabendaten bei Eingabeänderungen
     * 
     * @param {Object} e - Das Event-Objekt
     */
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTaskData({
            ...taskData,
            [name]: value
        });
    };

    /**
     * Fügt eine neue Aufgabe hinzu
     */
    const handleAddTask = async () => {
        try {
            await addTask(taskData);
            
            setIsAddDialogOpen(false);
            fetchTasks();
            resetTaskData();
        } catch (error) {
            console.error('Fehler beim Hinzufügen der Aufgabe:', error);
        }
    };

    /**
     * Aktualisiert eine bestehende Aufgabe
     */
    const handleEditTask = async () => {
        try {
            await updateTask(currentTaskId, taskData);
            
            setIsEditDialogOpen(false);
            fetchTasks();
        } catch (error) {
            console.error('Fehler beim Bearbeiten der Aufgabe:', error);
        }
    };

    /**
     * Löscht eine Aufgabe
     */
    const handleDeleteTask = async () => {
        try {
            await deleteTask(currentTaskId);
            
            setIsEditDialogOpen(false);
            fetchTasks();
        } catch (error) {
            console.error('Fehler beim Löschen der Aufgabe:', error);
        }
    };

    /**
     * Öffnet den Bearbeitungsdialog für eine Aufgabe
     * 
     * @param {Object} task - Die zu bearbeitende Aufgabe
     */
    const openEditDialog = (task) => {
        setTaskData({
            name: task.name,
            hourlyRate: task.hourlyRate,
            dailyRate: task.dailyRate,
        });
        setCurrentTaskId(task._id);
        setIsEditDialogOpen(true);
    };
    
    /**
     * Setzt die Aufgabendaten zurück
     */
    const resetTaskData = () => {
        setTaskData({
            name: '',
            hourlyRate: '',
            dailyRate: ''
        });
    };

    /**
     * Schließt den Dialogfenster und setzt Daten zurück (für den Erstellungsdialog)
     */
    const handleCancelAddDialog = () => {
        setIsAddDialogOpen(false);
        resetTaskData();
    };

    /**
     * Schließt den Bearbeitungsdialog
     */
    const handleCancelEditDialog = () => {
        setIsEditDialogOpen(false);
    };

    return (
        <div className="bg-gray-50 min-h-screen pt-[48px] px-[83px]">
            {/* Header */}
            <div className="mb-[32px]">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold">Aufgaben</h1>
                    <Button
                        variant="primary"
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        + Aufgabe hinzufügen
                    </Button>
                </div>
                <p className="text-sm text-gray-600">Übersicht der Aufgaben</p>
            </div>

            {/* Hauptinhalt: Aufgabenliste */}
            <TaskList
                tasks={tasks}
                loading={loading}
                error={error}
                onEditTask={openEditDialog}
            />

            {/* Dialog zum Hinzufügen einer neuen Aufgabe */}
            <TaskDialog
                isOpen={isAddDialogOpen}
                isEditMode={false}
                taskData={taskData}
                onInputChange={handleInputChange}
                onSave={handleAddTask}
                onCancel={handleCancelAddDialog}
            />

            {/* Dialog zum Bearbeiten einer bestehenden Aufgabe */}
            <TaskDialog
                isOpen={isEditDialogOpen}
                isEditMode={true}
                taskData={taskData}
                onInputChange={handleInputChange}
                onSave={handleEditTask}
                onCancel={handleCancelEditDialog}
                onDelete={handleDeleteTask}
            />
        </div>
    );
};

export default Tasks;
